import { format, isValid, parse } from 'date-fns';
import { useEffect, useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

interface TimePickerProps {
  /*
   * The value of the time picker in the format 'hh:mm AM/PM'.
   */
  value: string;
  /*
   * The function to call when the time picker value
   * changes. The new value is passed as a string in the
   * format 'hh:mm AM/PM'.
   * @param time - The new time value.
   * @returns void
   */
  onChange: (time: string) => void;
}

function parseTime(timeStr: string): Date | null {
  const formats = [
    'HH:mm', // 24-hour format (e.g., 14:30)
    'h:mm a', // 12-hour format (e.g., 2:30 PM)
  ];

  for (const fmt of formats) {
    const parsedDate = parse(timeStr, fmt, new Date());
    if (isValid(parsedDate)) {
      return parsedDate; // Return in 24-hour format
    }
  }

  return null; // Return null if the time is invalid
}

export function TimePickerDropdown({ value, onChange }: TimePickerProps) {
  const [hour, setHour] = useState<string>('12');
  const [minute, setMinute] = useState<string>('00');
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');

  useEffect(() => {
    if (value) {
      const date = parseTime(value);
      if (date) {
        setHour(format(date, 'hh'));
        setMinute(format(date, 'mm'));
        setPeriod(format(date, 'a') as 'AM' | 'PM');
      }
    }
  }, [value]);

  const handleChange = (
    newHour: string,
    newMinute: string,
    newPeriod: 'AM' | 'PM',
  ) => {
    // Convert to 24hr format
    let hour24 = parseInt(newHour, 10);
    if (newPeriod === 'PM' && hour24 !== 12) hour24 += 12;
    if (newPeriod === 'AM' && hour24 === 12) hour24 = 0;

    const timeString = `${hour24.toString().padStart(2, '0')}:${newMinute}`;
    onChange(timeString);
  };

  return (
    <div className='flex w-full min-w-0 gap-2'>
      <Select
        value={hour}
        onValueChange={(newHour) => {
          setHour(newHour);
          handleChange(newHour, minute, period);
        }}
      >
        <SelectTrigger className='min-w-0 flex-1 px-2'>
          <SelectValue placeholder='Hrs' />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 12 }, (_, i) =>
            (i + 1).toString().padStart(2, '0'),
          ).map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={minute}
        onValueChange={(newMinute) => {
          setMinute(newMinute);
          handleChange(hour, newMinute, period);
        }}
      >
        <SelectTrigger className='min-w-0 flex-1 px-2'>
          <SelectValue placeholder='Mins' />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 60 }, (_, i) =>
            i.toString().padStart(2, '0'),
          ).map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={period}
        onValueChange={(newPeriod: 'AM' | 'PM') => {
          setPeriod(newPeriod);
          handleChange(hour, minute, newPeriod);
        }}
      >
        <SelectTrigger className='w-[4.5rem] shrink-0 px-2'>
          <SelectValue placeholder='AM/PM' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value='AM'>AM</SelectItem>
          <SelectItem value='PM'>PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
